<script setup lang="ts">
  // Dynamic-dialog picker for reassigning a caja movement/payment's method.
  // Opened via modals.showDynamicDialog<PaymentMethod | null>(
  //   ModalChangePaymentMethod, { currentMethod }, 'change-payment-method');
  // resolves with the chosen method, or null when dismissed.
  import { computed } from 'vue';
  import { PaymentMethod } from '@/enums/PaymentMethod';
  import {
    paymentMethodOptions,
    REASSIGNABLE_PAYMENT_METHODS,
  } from '@/domain/payments/payment-method-lists';
  import { useModal } from '@/composition/use-modal';

  const props = defineProps<{
    currentMethod: PaymentMethod | null;
  }>();

  const modals = useModal();

  const options = computed(() =>
    paymentMethodOptions(REASSIGNABLE_PAYMENT_METHODS).map((option) => ({
      ...option,
      // Both APIs reject same-method changes: prevent it in the UI.
      isCurrent: option.value === props.currentMethod,
    }))
  );

  function selectMethod(method: PaymentMethod) {
    // dialogSubmit unmounts this modal: never mutate local state after it
    // (renderer-safety rule).
    modals.dialogSubmit('change-payment-method', method);
  }

  function cancel() {
    modals.hide('change-payment-method');
  }
</script>

<template>
  <v-card class="change-method-modal" elevation="0">
    <v-card-title class="modal-title">Cambiar medio de pago</v-card-title>
    <v-card-text class="modal-body">
      <p class="modal-subtitle">Seleccione el nuevo medio de pago</p>
      <div class="payment-methods">
        <div
          v-for="option in options"
          :key="option.value"
          class="payment-method"
          :class="{ current: option.isCurrent }"
          @click="!option.isCurrent && selectMethod(option.value)"
        >
          <span class="method-label">{{ option.text }}</span>
          <span v-if="option.isCurrent" class="current-tag">Actual</span>
        </div>
      </div>
    </v-card-text>
    <v-card-actions class="modal-actions">
      <v-spacer />
      <v-btn text @click="cancel">Cancelar</v-btn>
    </v-card-actions>
  </v-card>
</template>

<style scoped lang="scss">
  .change-method-modal {
    .modal-title {
      @apply tw-pb-1;
    }

    .modal-body {
      .modal-subtitle {
        @apply tw-mb-3 tw-text-sm tw-text-gray-500;
      }

      // Mirrors the ModalPay method-tile look so both pickers feel the same.
      .payment-methods {
        @apply tw-grid tw-grid-cols-4 tw-gap-2;

        .payment-method {
          @apply tw-flex tw-h-16 tw-cursor-pointer tw-flex-col tw-items-center tw-justify-center tw-rounded-lg tw-border tw-border-gray-300 tw-p-4 tw-text-center tw-text-base tw-text-gray-800;

          &:hover {
            @apply tw-bg-gray-100;
          }

          &.current {
            @apply tw-pointer-events-none tw-bg-gray-100 tw-text-gray-400;
          }

          .method-label {
            @apply tw-leading-tight;
          }

          .current-tag {
            @apply tw-text-[10px] tw-font-bold tw-uppercase tw-tracking-wide;
          }
        }
      }
    }

    .modal-actions {
      @apply tw-px-4 tw-pb-3;
    }
  }
</style>
